import axios from 'axios';

// 创建axios实例
const instance = axios.create({
  baseURL: 'http://books.c1.phym.cloud/api',  // 根据实际部署环境修改
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
instance.interceptors.request.use(
  config => {
    // 添加token到请求头
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// 响应拦截器
instance.interceptors.response.use(
  response => {
    // 处理成功响应
    return response.data;
  },
  error => {
    // 处理错误响应
    if (error.response) {
      // 服务器返回错误
      const { status } = error.response;
      
      // 对于401错误（未授权），不再自动跳转，让各页面自行处理
      if (status === 401 && !error.config.url.includes('/login.php')) {
        // 仅清除token，不自动跳转
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // 为非登录接口的401错误自动跳转
        location.href = '/login';
      }
    } else if (error.request) {
      // 请求已发出但未收到响应
      console.error('服务器未响应');
    } else {
      // 请求配置异常
      console.error('请求异常:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API方法封装
export const api = {
  // 登录
  login: (data) => {
    return instance.post('/login.php', data);
  },
  
  // 注册
  register: (data) => {
    return instance.post('/register.php', data);
  },
  
  // 获取用户信息
  getUserInfo: () => {
    return instance.get('/get_user_info.php');
  },
  
  // 更新用户个人信息
  updateProfile: (data) => {
    return instance.post('/update_profile.php', data);
  },
  
  // 获取用户详细信息
  getProfile: () => {
    return instance.get('/get_profile.php');
  },
  
  // 修改用户密码
  changePassword: (data) => {
    return instance.post('/change_password.php', data);
  },
  
  // 获取轮播图
  getCarousel: () => {
    return instance.get('/get_carousel.php');
  },
  
  // 获取热门推荐产品
  getHotProducts: () => {
    return instance.get('/get_hot_products.php');
  },
  
  // 获取护肤攻略
  getSkincareTips: () => {
    return instance.get('/get_skincare_tips.php');
  },
  
  // 获取公告
  getAnnouncements: () => {
    return instance.get('/get_announcements.php');
  },
  
  // 获取产品列表
  getProducts: (params = {}) => {
    return instance.get('/get_products.php', { params });
  },
  
  // 获取产品详情
  getProductDetail: (id) => {
    return instance.get(`/get_product_detail.php?id=${id}`);
  },
  
  // 收藏物品
  collectItem: (itemId, itemType = 'product') => {
    return instance.post('/collect_item.php', { item_id: itemId, item_type: itemType });
  },
  
  // 取消收藏
  uncollectItem: (itemId, itemType = 'product') => {
    return instance.post('/uncollect_item.php', { item_id: itemId, item_type: itemType });
  },
  
  // 获取收藏列表
  getCollections: (type = 'product') => {
    return instance.get('/get_collections.php', { params: { type } });
  },
  
  // 检查是否已收藏
  checkCollection: (itemId, itemType = 'product') => {
    return instance.get('/check_collection.php', { 
      params: { item_id: itemId, item_type: itemType } 
    });
  },
  
  // 获取笔记列表
  getNotes: (params = {}) => {
    return instance.get('/get_notes.php', { params });
  },
  
  // 获取用户个人笔记列表
  getMyNotes: (page = 1, limit = 10) => {
    return instance.get('/get_my_notes.php', { 
      params: { page, limit } 
    });
  },
  
  // 创建笔记
  createNote: (data) => {
    return instance.post('/create_note.php', data);
  },
  
  // 更新笔记
  updateNote: (id, data) => {
    return instance.post('/update_note.php', { id, ...data });
  },
  
  // 删除笔记
  deleteNote: (noteId) => {
    return instance.post('/delete_note.php', {
      id: noteId
    });
  },
  
  // 上传图片
  uploadImage: (formData, onProgress) => {
    return instance.post('/upload_image.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress
    });
  },
  
  // 管理员专用上传图片（不验证身份）
  adminUploadImage: (formData, onProgress) => {
    return instance.post('/admin_upload_image.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress
    });
  },
  
  // 获取护肤知识列表
  getKnowledge: (params = {}) => {
    return instance.get('/get_knowledge.php', { params });
  },
  
  // 获取护肤知识详情
  getKnowledgeDetail: (id) => {
    return instance.get(`/get_knowledge_detail.php?id=${id}`);
  },
  
  // 获取护肤笔记详情
  getNoteDetail: (id) => {
    return instance.get(`/get_note_detail.php?id=${id}`);
  },
  
  // 获取笔记评论
  getNoteComments: (noteId, page = 1, limit = 10) => {
    return instance.get('/get_note_comments.php', {
      params: {
        note_id: noteId,
        page: page,
        limit: limit
      }
    });
  },
  
  // 添加笔记评论
  addNoteComment: (noteId, content, rating = 5.0) => {
    return instance.post('/add_note_comment.php', {
      note_id: noteId,
      content: content,
      rating: rating
    });
  },
  
  // 获取评论列表
  getComments: (knowledgeId, params = {}) => {
    return instance.get('/get_comments.php', {
      params: {
        knowledge_id: knowledgeId,
        ...params
      }
    });
  },
  
  // 添加评论
  addComment: (data) => {
    return instance.post('/add_comment.php', data);
  },
  
  // 点赞文章
  likeKnowledge: (knowledgeId) => {
    return instance.post('/like_knowledge.php', {
      knowledge_id: knowledgeId
    });
  },
  
  // 获取用户列表
  getUsers: (params = {}) => {
    return instance.get('/get_users.php', { params });
  },
  
  // 删除用户
  deleteUser: (id) => {
    return instance.post('/delete_user.php', { id });
  },
  
  // 获取轮播图列表
  getCarouselList: (params) => {
    return instance.get('/get_carousel_list.php', { params });
  },
  
  // 添加轮播图
  addCarousel: (formData) => {
    return instance.post('/add_carousel.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 更新轮播图
  updateCarousel: (formData) => {
    return instance.post('/update_carousel.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 删除轮播图
  deleteCarousel: (id) => {
    return instance.post('/delete_carousel.php', { id });
  },
  
  // 获取公告列表（管理端）
  getAnnouncementsList: (params = {}) => {
    return instance.get('/end_get_announcements.php', { params });
  },
  
  // 更新公告(新增或编辑)
  updateAnnouncement: (formData) => {
    return instance.post('/end_update_announcement.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 删除公告
  deleteAnnouncement: (id) => {
    return instance.post('/delete_announcement.php', { id });
  },
  
  // 获取产品列表（管理端）
  getProductsList: (params = {}) => {
    return instance.get('/end_get_products.php', { params });
  },
  
  // 添加/更新产品（统一接口）
  updateProduct: (formData) => {
    return instance.post('/end_update_product.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 删除产品
  deleteProduct: (id) => {
    return instance.post('/delete_product.php', { id });
  },
  
  // 获取笔记列表（管理端）
  getNotesList: (params = {}) => {
    return instance.get('/end_get_notes.php', { params });
  },
  
  // 添加笔记
  addNote: (formData) => {
    return instance.post('/end_add_note.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  updateNoteStatus(data) {
    return this.post('/end_update_note.php', data);
  },
  
  // 更新笔记
  updateNoteAdmin: (formData) => {
    return instance.post('/end_update_note.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 删除笔记
  deleteNoteAdmin: (id) => {
    return instance.post('/end_delete_note.php', { id });
  },
  
  // 获取护肤知识列表（管理端）
  getKnowledgeList: (params = {}) => {
    return instance.get('/end_get_knowledge.php', { params });
  },
  
  // 添加/更新护肤知识（统一接口）
  updateKnowledge: (formData) => {
    return instance.post('/end_update_knowledge.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // 删除护肤知识
  deleteKnowledge: (id) => {
    return instance.post('/end_delete_knowledge.php', { id });
  },
  
  // 点赞相关接口
  likeItem: (itemId, itemType = 'product') => {
    return instance.post('/like_item.php', {
      item_id: itemId,
      item_type: itemType
    });
  },
  
  // 检查点赞状态
  checkLikeStatus: (itemId, itemType = 'product') => {
    return instance.get('/check_like_status.php', {
      params: {
        item_id: itemId,
        item_type: itemType
      }
    });
  },
  
  // 产品评论相关接口
  addProductComment: (productId, content, rating = 5) => {
    return instance.post('/add_product_comment.php', {
      product_id: productId,
      content,
      rating
    });
  },
  
  // 获取产品评论
  getProductComments: (productId, page = 1, limit = 10) => {
    return instance.get(`/get_product_comments.php?product_id=${productId}&page=${page}&limit=${limit}`);
  },
  
  // 获取知识文章评论
  getKnowledgeComments: (knowledgeId, page = 1, limit = 10) => {
    return instance.get('/get_knowledge_comments.php', {
      params: {
        knowledge_id: knowledgeId,
        page: page,
        limit: limit
      }
    });
  },
  
  // 添加知识文章评论
  addKnowledgeComment: (knowledgeId, content, rating = 5.0) => {
    return instance.post('/add_knowledge_comment.php', {
      knowledge_id: knowledgeId,
      content: content,
      rating: rating
    });
  },
  
  // 获取所有评论（管理端）
  getAllComments: (params = {}) => {
    return instance.get('/get_all_comments.php', { params });
  },
  
  // 删除评论（管理端）
  deleteComment: (id, type) => {
    return instance.post('/delete_comment.php', {
      id: id,
      type: type
    });
  }
};

// 工具方法
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete instance.defaults.headers.common['Authorization'];
  }
};

export default instance;